import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import {
  getCompanyProfileInfo,
  getCompanySetting,
  getJobRoles,
  getJobPositions,
  getDepartmentByCompanyId,
  getDepartmentUsersByCompanyId,
  getUserList,
  getMyCompanyDefault,
  getUserAuthority,
} from '@mygpt/app/apis';

interface PilotStoreInterface {
  companyProfileInfo?: any | undefined;
  adminInfo?: any | undefined;
  jobRolesList?: any | undefined;
  jobPositionList?: any | undefined;
  companyId?: any | undefined;
  companyName?: string | undefined;
  companyDescription?: string | undefined;
  companySetting?: any | undefined;
  departmentList?: any | undefined;
  departmentUsersList?: any | undefined;
  userList?: any | undefined;
  usersRes?: any | undefined;
  hasAdminConsoleAuthority?: boolean;
  needsDefaultPasswordChange?: boolean;
  // fetchUserInfoForAdmin: () => Promise<any>;
  fetchCompanyProfileInfo: (params: { companyId: string }) => Promise<any>;
  fetchCompanySetting: (params: { companyId: string }) => Promise<any>;
  fetchJobRoles: (params: { lang: string }) => Promise<any>;
  fetchJobPosition: (params: { lang: string }) => Promise<any>;
  fetchCompanyId: () => Promise<any>;
  fetchDepartmentByCompanyId: (params: { companyId: string }) => Promise<any>;
  fetchUserList: (params: {
    currentPage: number;
    pageSize: number;
    companyId: string;
    inCompanyUserNameInfixLike?: string;
    enabled?: boolean;
    userCompanyEnabled?: boolean;
  }) => Promise<any>;
}
// 定义部门的类型
interface Department {
  id: string;
  name: string;
  children?: Department[];
  members?: any[];
  totalMembers?: number;
}

export const useAdminStore = create<PilotStoreInterface>()(
  immer((set, get) => ({
    // fetchUserInfoForAdmin: () =>
    //   new Promise<any>((resolve, reject) => {
    //     getUserInfoForAdmin({
    //       email: 'admin@gbase.ai',
    //       password: '123456',
    //     }).then(({ data }) => {
    //       if (!data) return;
    //       set({
    //         adminInfo: data,
    //       });
    //       resolve(data);
    //     });
    //   }),
    fetchCompanyId: () =>
      new Promise<any>((resolve, reject) => {
        getMyCompanyDefault().then(({ data }) => {
          if (data.success) {
            set({
              companyId: data.company.id,
              companyName: data.company.name,
              companyDescription: data.company.description,
              needsDefaultPasswordChange: data.userSecurity.mustChangePassword,
            });
            getUserAuthority(data.company.id).then(({ data }) => {
              set({
                hasAdminConsoleAuthority:
                  data.authorityCodes.includes('ADMIN_CONSOLE') &&
                  (window as any).ENV?.GBASE_ADMIN_CONSOLE_SHOW === 'true',
              });
            });
          } else {
            if (data.error.name === 'JWT_TOKEN_EXPIRED') {
              console.log('JWT_TOKEN_EXPIRED');
            }
            set({
              hasAdminConsoleAuthority: false,
            });
          }
        });
      }),
    fetchCompanySetting: (params: { companyId: string }) =>
      new Promise<any>((resolve, reject) => {
        getCompanySetting(params.companyId).then(({ data }) => {
          console.log('fetchCompanySetting', data);
          set({
            companySetting: data.companySetting,
          });
          resolve(data);
        });
      }),
    fetchCompanyProfileInfo: (params: { companyId: string }) =>
      new Promise<any>((resolve, reject) => {
        getCompanyProfileInfo(params.companyId).then(({ data }) => {
          console.log('fetchCompanyProfileInfo', data);
          if (data.success) {
            set({
              companyProfileInfo: data.companyProfile,
            });
            resolve(data);
          }
        });
      }),

    fetchJobRoles: (params: { lang: string }) =>
      new Promise<any>((resolve, reject) => {
        getJobRoles(params).then(({ data }) => {
          if (!data) return;
          console.log('res', data);
          set({
            jobRolesList: data.jobRoles,
          });
          resolve(data);
        });
      }),
    fetchJobPosition: (params: { lang: string }) =>
      new Promise<any>((resolve, reject) => {
        getJobPositions(params).then(({ data }) => {
          if (!data) return;
          set({
            jobPositionList: data.jobPositions,
          });
          resolve(data);
        });
      }),
    fetchDepartmentByCompanyId: (params: { companyId: string }) =>
      new Promise<any>((resolve, reject) => {
        Promise.all([getDepartmentByCompanyId(params.companyId), getDepartmentUsersByCompanyId(params.companyId)])
          .then(([departmentRes, usersRes]) => {
            set({
              usersRes: usersRes,
            });
            const departmentList = departmentRes.departments || [];
            const departmentIdToUserIdsMap = usersRes.departmentIdToUserIdsMap || {};
            const users = usersRes.users || [];

            const mapUsersToDepartments = (departments: Department[]): Department[] => {
              return departments.map((department) => {
                const userIds = departmentIdToUserIdsMap[department.id] || [];
                const departmentUsers = userIds
                  .map((userId) => users.find((user) => user.id === userId))
                  .filter(Boolean);

                const children = mapUsersToDepartments(department.children || []);
                const totalMembers = departmentUsers.length + getTotalMembers(children);

                return {
                  ...department,
                  members: departmentUsers,
                  children: children,
                  totalMembers: totalMembers,
                };
              });
            };

            function getTotalMembers(departments: Department[]): number {
              return departments.reduce((total, dept) => {
                const memberCount = Array.isArray(dept.members) ? dept.members.length : 0;
                const childrenMembers = dept.children ? getTotalMembers(dept.children) : 0;
                return total + memberCount + childrenMembers;
              }, 0);
            }

            const departmentsWithUsers = mapUsersToDepartments(departmentList);

            set({
              departmentList: departmentsWithUsers,
              departmentUsersList: users,
            });

            console.log('Updated departments:', departmentsWithUsers);
            resolve(departmentsWithUsers);
          })
          .catch((error) => {
            console.error('Error in fetchDepartmentByCompanyId:', error);
            reject(error);
          });
      }),
    fetchUserList: (params: {
      currentPage: number;
      pageSize: number;
      companyId: string;
      inCompanyUserNameInfixLike?: string;
      enabled?: boolean;
      userCompanyEnabled?: boolean;
    }) =>
      new Promise<any>((resolve, reject) => {
        getUserList(params).then(({ data }) => {
          set({
            userList: data,
          });
          resolve(data);
        });
      }),
  }))
);
